import { useCallback, useMemo } from 'react';
import type {
  CustomChartDefinition,
  CustomChartTableSettings,
  DashboardPreset,
} from '@/entities/dashboards/model/api/types';
import { useSettings } from './useSettings';

/**
 * Хук работы с пользовательскими дашбордами-пресетами страницы дашбордов.
 *
 * Пресет здесь — самостоятельный состав диаграмм, а не снимок текущего вида экрана: создание
 * заводит новый дашборд, а правка меняет его состав. Поэтому «сохранить текущее состояние в
 * пресет» — операции, обратной применению, — у дашбордов нет.
 */
export const useDashboardPresets = () => {
  const {
    userId,
    dashboardPresets,
    createDashboardPreset,
    updateDashboardPreset,
    applyDashboardPreset,
    clearActiveDashboardPreset,
    deleteDashboardPreset,
    setDashboardPresetPinned,
    setDashboardChartTableSettings,
  } = useSettings();

  const presets = useMemo<DashboardPreset[]>(() => {
    const list = Object.values(dashboardPresets.presets ?? {});
    list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return list;
  }, [dashboardPresets.presets]);

  const activePresetId = dashboardPresets.activePresetId;
  const activePreset = useMemo(
    () => presets.find((preset) => preset.id === activePresetId) ?? null,
    [presets, activePresetId],
  );

  const createPreset = useCallback(
    (name: string, charts: CustomChartDefinition[]): string => createDashboardPreset(userId, name, charts),
    [createDashboardPreset, userId],
  );

  const updatePreset = useCallback(
    (presetId: string, changes: { name?: string; charts?: CustomChartDefinition[] }) =>
      updateDashboardPreset(userId, presetId, changes),
    [updateDashboardPreset, userId],
  );

  const apply = useCallback(
    (presetId: string) => applyDashboardPreset(userId, presetId),
    [applyDashboardPreset, userId],
  );

  const clearActive = useCallback(() => clearActiveDashboardPreset(userId), [clearActiveDashboardPreset, userId]);

  const remove = useCallback(
    (presetId: string) => deleteDashboardPreset(userId, presetId),
    [deleteDashboardPreset, userId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => setDashboardPresetPinned(userId, presetId, pinned),
    [setDashboardPresetPinned, userId],
  );

  const chartTables = dashboardPresets.chartTables;

  const setChartTableSettings = useCallback(
    (chartId: string, settings: Partial<CustomChartTableSettings>) =>
      setDashboardChartTableSettings(userId, chartId, settings),
    [setDashboardChartTableSettings, userId],
  );

  return {
    presets,
    activePresetId,
    activePreset,
    /** Личный вид таблиц диаграмм по идентификатору диаграммы */
    chartTables,
    createPreset,
    updatePreset,
    apply,
    clearActive,
    remove,
    setPinned,
    setChartTableSettings,
  };
};
